import { useCallback, useMemo, useState } from "react"
import { useClient } from "urql"

import { usePurchaseOrderUpsert } from "../../hooks/usePurchaseOrderUpsert"
import type { ProductSelection, ProductSelectionItem, PurchaseOrderFormData } from "../types/types"

import type { Company, Product } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { validatePurchaseOrderCreation } from "@/screens/Procurement/PurchaseOrder/utils/validatePurchaseOrderCreation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseSupplierReportPurchaseOrderProps {
  data: Company[]
  productRowSelection: ProductSelection
}

interface UseSupplierReportPurchaseOrderReturn {
  fetching: boolean
  shippingLocationMap: Record<string, ProductSelectionItem[]>
  isPurchaseOrderDialogOpen: boolean
  selectedCompany: Company | undefined
  selectedCompanyProducts: ProductSelectionItem[]
  handleStartPO: (companyId: string) => void
  handleCloseDialog: () => void
  handleSubmitPO: (data: PurchaseOrderFormData) => void
}

export const useSupplierReportPurchaseOrder = ({
  data,
  productRowSelection,
}: UseSupplierReportPurchaseOrderProps): UseSupplierReportPurchaseOrderReturn => {
  const [isPurchaseOrderDialogOpen, setPurchaseOrderDialogOpen] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>()

  const { handleCreatePO, fetching } = usePurchaseOrderUpsert()
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)
  const client = useClient()

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) return undefined
    return data.find((company) => company.companyId === selectedCompanyId)
  }, [data, selectedCompanyId])

  const selectedCompanyProducts = useMemo(() => {
    if (!selectedCompanyId) return []
    return Object.values(productRowSelection[selectedCompanyId] || {})
  }, [productRowSelection, selectedCompanyId])

  const selectedProducts = useMemo(() => {
    return Object.values(productRowSelection).flatMap((companyProducts) => Object.values(companyProducts))
  }, [productRowSelection])

  // Creates a map of products with same shippingId in locationsAssociations of a product
  const shippingLocationMap = useMemo(() => {
    return selectedProducts.reduce(
      (acc, product) => {
        if (product.locationsAssociations.length > 0) {
          const shippingId = product.locationsAssociations[0].shippingId
          // eslint-disable-next-line eqeqeq
          if (shippingId != null) {
            acc[shippingId] = (acc[shippingId] || []).concat(product)
          }
        }
        return acc
      },
      {} as Record<string, ProductSelectionItem[]>
    )
  }, [selectedProducts])

  const handleStartPO = useCallback(
    async (companyId: string) => {
      const products = Object.values(productRowSelection[companyId] || {})

      const validation = await validatePurchaseOrderCreation({
        products: products as Product[],
        companyId,
        client,
      })

      if (!validation.isValid) {
        notify({
          type: NotificationType.Warning,
          content: validation.message!,
        })
        return
      }

      setSelectedCompanyId(companyId)
      setPurchaseOrderDialogOpen(true)
    },
    [productRowSelection, client, notify]
  )

  const handleCloseDialog = useCallback(() => {
    setPurchaseOrderDialogOpen(false)
    setSelectedCompanyId(undefined)
  }, [setPurchaseOrderDialogOpen, setSelectedCompanyId])

  const handleSubmitPO = (data: PurchaseOrderFormData) => {
    handleCreatePO(data, selectedCompanyProducts)
    handleCloseDialog()
  }

  return {
    fetching,
    shippingLocationMap,
    isPurchaseOrderDialogOpen,
    selectedCompany,
    selectedCompanyProducts,
    handleStartPO,
    handleCloseDialog,
    handleSubmitPO,
  }
}
